import { useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverAnchor } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { searchTokens, matchesTokens } from "@/lib/search";

/**
 * Type it or pick it. For text that repeats but is not a foreign key.
 *
 * The distinction that decides which control a field gets: a **unit** or a **trainer** is
 * a row in a table, so it is a hard picker and nothing else may be entered. A **venue**,
 * a **designation** or an **agency** is just text that happens to repeat — HR types
 * "Training Hall 1" for the fortieth time — so it must offer what has been used before
 * and still accept something new. A hard dropdown there would block the first training
 * held in a new room, which is not a rule anybody agreed to.
 *
 * The value is the string itself; there are no ids. `options` is `string[]`.
 */

const FIELD = [(s) => s];

export default function ComboBox({
  value = "",
  onChange,
  options = [],
  placeholder = "",
  /** Shown under the box when the list is empty — usually "nothing recorded yet". */
  emptyHint,
  id,
  testid,
  disabled = false,
  className,
  /** The server's own limit, so the box stops where the API would refuse. */
  maxLength,
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef(null);
  const anchorRef = useRef(null);

  // Suggestions are filtered by what has been typed, but an exact match is not hidden —
  // seeing the value you just typed already on the list is how you know it is not a
  // fortieth spelling of something that exists.
  const tokens = useMemo(() => searchTokens(value), [value]);
  const shown = useMemo(
    () => options.filter((o) => matchesTokens(o, tokens, FIELD)).slice(0, 50),
    [options, tokens]);

  const commit = (v) => {
    onChange(v);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((i) => Math.max(0, Math.min(shown.length - 1, i + step)));
      return;
    }
    // Enter picks the highlighted suggestion, but only when one is highlighted: otherwise
    // it must leave the typed text alone, or a new venue turns into a similar old one.
    if (e.key === "Enter" && open && active >= 0 && shown[active]) {
      e.preventDefault();
      commit(shown[active]);
      return;
    }
    if (e.key === "Escape" && open) { e.preventDefault(); setOpen(false); setActive(-1); }
  };

  return (
    <Popover open={open && (shown.length > 0)} onOpenChange={(o) => { if (!o) { setOpen(false); setActive(-1); } }}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} className={cn("relative", className)}>
          <Input
            ref={inputRef}
            id={id}
            data-testid={testid}
            value={value}
            disabled={disabled}
            maxLength={maxLength}
            placeholder={placeholder}
            aria-label={placeholder || undefined}
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            autoComplete="off"
            className="pr-9"
            onChange={(e) => { onChange(e.target.value); setOpen(true); setActive(-1); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
          />
          {options.length > 0 && (
            <button
              type="button"
              tabIndex={-1}
              aria-label="Show suggestions"
              disabled={disabled}
              onClick={() => { setOpen((o) => !o); inputRef.current?.focus(); }}
              className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] min-w-[200px] p-0"
        align="start"
        // Focus must stay in the box: this is a text field that suggests, not a menu.
        onOpenAutoFocus={(e) => e.preventDefault()}
        // Radix excludes a *Trigger* from its outside-click detection, but not an
        // *Anchor* — and this has to be an anchor, because the thing being anchored to is
        // the text box itself and a text box cannot also be a button. Without this, every
        // click and keystroke in the input counted as an interaction outside the popover
        // and closed it on the same tick it opened, so the suggestions never appeared.
        onInteractOutside={(e) => {
          if (anchorRef.current?.contains(e.target)) e.preventDefault();
        }}
      >
        <ul className="max-h-56 overflow-y-auto py-1" role="listbox">
          {shown.map((o, i) => (
            <li key={o}>
              <button
                type="button"
                role="option"
                aria-selected={o === value}
                data-testid={testid ? `${testid}-option-${i}` : undefined}
                onMouseDown={(e) => { e.preventDefault(); commit(o); }}
                onMouseEnter={() => setActive(i)}
                className={cn(
                  "w-full text-left px-3 py-2 text-sm flex items-center gap-2",
                  i === active ? "bg-muted" : "hover:bg-muted",
                )}
              >
                <Check className={cn("w-3.5 h-3.5 shrink-0", o === value ? "opacity-100" : "opacity-0")} />
                <span className="truncate">{o}</span>
              </button>
            </li>
          ))}
        </ul>
        {emptyHint && shown.length === 0 && (
          <p className="px-3 py-2 text-xs text-muted-foreground">{emptyHint}</p>
        )}
      </PopoverContent>
    </Popover>
  );
}
